import { useCallback, useEffect, useRef, useState } from 'react';
import { candidacyService } from '@/shared/services/candidacyService';

/**
 * The signed-in student's candidacies and commitments, from
 * GET /api/candidacy/mine. With `skip` no request is made.
 *
 * @returns {{ candidacies: object[], commitments: object, loading: boolean,
 *   error: boolean, reload: () => void, replace: (candidacy) => void }}
 * `replace` swaps the candidacy with the same id, or appends a new one.
 * Once a read has succeeded, `reload` refreshes in the background: the lists
 * stay mounted, and a failed refresh keeps what is shown.
 */
export default function useMyCandidacies({ skip = false } = {}) {
    const [candidacies, setCandidacies] = useState([]);
    const [commitments, setCommitments] = useState({});
    const [loading, setLoading] = useState(!skip);
    const [error, setError] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const loadedRef = useRef(false);

    useEffect(() => {
        if (skip) {
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        if (!loadedRef.current) {
            setLoading(true);
            setError(false);
        }
        candidacyService
            .getMine()
            .then((data) => {
                if (cancelled) return;
                setCandidacies(data.candidacies || []);
                setCommitments(data.commitments || {});
                loadedRef.current = true;
                setError(false);
                setLoading(false);
            })
            .catch(() => {
                if (cancelled || loadedRef.current) return;
                setError(true);
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [skip, attempt]);

    const reload = useCallback(() => setAttempt((n) => n + 1), []);

    const replace = useCallback((candidacy) => {
        setCandidacies((current) =>
            current.some((c) => c.id === candidacy.id)
                ? current.map((c) => (c.id === candidacy.id ? candidacy : c))
                : [...current, candidacy]
        );
    }, []);

    return { candidacies, commitments, loading, error, reload, replace };
}
